import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { legacyArmoveLine } from '@hopdrive/sdk-ar';
import { toast } from 'react-toastify';
import { ExportToCsv } from 'export-to-csv';
import { getFormattedCancelStatusFromDrive, getFormattedStatusFromDrive } from '@services/statusService';
import { getCsvMonetaryValue } from '@services/formatService';
import { GetInvoiceDetailsQuery } from '../queries/getInvoiceDetails.query.generated';
import { describeLegacyRowName, isLegacyMoveRow, isOtherLegacyArmove } from './legacyArmoves';

dayjs.extend(utc);

type LegacyInvoice = NonNullable<GetInvoiceDetailsQuery['arinvoices_by_pk']>;
type LegacyArmove = LegacyInvoice['armoves'][number];

export { isOtherLegacyArmove };

const legacyCost = (armove: LegacyArmove) =>
  armove.due_amount
    ? `${getCsvMonetaryValue(Math.max(legacyArmoveLine(armove).net, 0))}`
    : armove.due_amount === 0
      ? getCsvMonetaryValue(0)
      : '-';

/** What a non-move row's LANE says: its type and the name it was billed under, as the products table shows it. */
const describeOtherArmove = (armove: LegacyArmove): string => {
  const type = armove.type ? armove.type.charAt(0).toUpperCase() + armove.type.slice(1) : 'Charge';
  const name = describeLegacyRowName(armove);
  return name === type ? type : `${type}: ${name}`;
};

/** The columns every legacy CSV row shares, for a row with no move. */
const otherArmoveColumns = (armove: LegacyArmove) => ({
  MOVE_ID: '-',
  REF_NUM: '-',
  VIN: '-',
  MOVE_DATE: armove.billable_datetime ? dayjs.utc(dayjs(armove.billable_datetime)).format('MM/DD/YYYY') : '-',
  STOCK: 'N/A',
  LANE: describeOtherArmove(armove),
  DISTANCE: '-',
  COST: legacyCost(armove),
});

/**
 * The standard CSV's rows: one per move, then one per other non-move row. Like
 * the charges invoice's standard CSV, it adds up to the total less products, so
 * product rows stay out. The Enhanced CSV lists them.
 */
export const buildLegacyCsvRows = (invoice: LegacyInvoice) => [
  ...invoice.armoves
    .filter(isLegacyMoveRow)
    .map(armove => ({
      MOVE_ID: armove?.move?.id,
      REF_NUM: armove?.move?.reference_num || '-',
      VIN: armove?.move?.vehicle_vin || '-',
      MOVE_DATE: dayjs.utc(dayjs(armove.billable_datetime)).format('MM/DD/YYYY'),
      STOCK: armove?.move?.vehicle_stock ? armove?.move?.vehicle_stock.toUpperCase() : 'N/A',
      LANE: armove?.move?.lane?.description || 'Unknown Lane',
      DISTANCE: armove?.move?.lane?.distance_miles ? `${armove.move.lane.distance_miles} mi` : '-',
      COST: legacyCost(armove),
      CANCEL_STATUS: armove?.move?.cancel_status ? `${getFormattedCancelStatusFromDrive(armove.move as never)}` : '-',
      MOVE_STATUS: armove?.move?.status ? `${getFormattedStatusFromDrive(armove.move as never)}` : '-',
      DISTANCE_MILES: armove?.move?.lane?.distance_miles || null,
    })),
  ...invoice.armoves
    .filter(armove => isOtherLegacyArmove(armove) && armove.type !== 'product')
    .map(armove => ({
      ...otherArmoveColumns(armove),
      CANCEL_STATUS: '-',
      MOVE_STATUS: '-',
      DISTANCE_MILES: null,
    })),
];

/** The v1 per-type columns an armove's details fill. */
const legacyBucketColumns = (armove: LegacyArmove) => ({
  TRANSPORT_TOTAL: armove?.transportDetails?.aggregate?.sum?.amount || 0,
  FUEL_TOTAL: armove?.fuelDetails?.aggregate?.sum?.amount || 0,
  FUEL_COUNT: armove?.fuelDetails?.aggregate?.count || 0,
  SURGE_TOTAL: armove?.surgeDetails?.aggregate?.sum?.amount || 0,
  SURGE_COUNT: armove?.surgeDetails?.aggregate?.count || 0,
  PARTIAL_TOTAL: armove?.partialDetails?.aggregate?.sum?.amount || 0,
  PARTIAL_COUNT: armove?.partialDetails?.aggregate?.count || 0,
  WAIVED_TOTAL: armove?.waivedDetails?.aggregate?.sum?.amount || 0,
  WAIVED_COUNT: armove?.waivedDetails?.aggregate?.count || 0,
  TOW_TOTAL: armove?.towDetails?.aggregate?.sum?.amount || 0,
  TOW_COUNT: armove?.towDetails?.aggregate?.count || 0,
  DELAY_TOTAL: armove?.delayDetails?.aggregate?.sum?.amount || 0,
  DELAY_COUNT: armove?.delayDetails?.aggregate?.count || 0,
  RIDE_TOTAL: armove?.rideDetails?.aggregate?.sum?.amount || 0,
  RIDE_COUNT: armove?.rideDetails?.aggregate?.count || 0,
});

/** The Enhanced CSV's rows: one per move with its per-type columns, then one per non-move row. */
export const buildLegacyEnhancedCsvRows = (invoice: LegacyInvoice) => [
  ...invoice.armoves
    .filter(isLegacyMoveRow)
    .map(armove => ({
      ROOFTOP: armove?.move?.customer?.name || '-',
      INVOICE_ID: armove.invoice_id || '-',
      MOVE_ID: armove?.move?.id,
      REF_NUM: armove?.move?.reference_num || '-',
      VIN: armove?.move?.vehicle_vin || '-',
      MOVE_DATE: dayjs.utc(dayjs(armove.billable_datetime)).format('MM/DD/YYYY'),
      STOCK: armove?.move?.vehicle_stock ? armove.move.vehicle_stock.toUpperCase() : 'N/A',
      LANE: armove?.move?.lane?.description || 'Unknown Lane',
      DISTANCE: armove?.move?.lane?.distance_miles ? `${armove.move.lane.distance_miles} mi` : '-',
      COST: legacyCost(armove),
      ...legacyBucketColumns(armove),
      CANCEL_STATUS: armove?.move?.cancel_status ? `${getFormattedCancelStatusFromDrive(armove.move as never)}` : '-',
      MOVE_STATUS: armove?.move?.status ? `${getFormattedStatusFromDrive(armove.move as never)}` : '-',
      DISTANCE_MILES: armove?.move?.lane?.distance_miles || null,
    })),
  ...invoice.armoves.filter(isOtherLegacyArmove).map(armove => {
    const { MOVE_ID, REF_NUM, VIN, MOVE_DATE, STOCK, LANE, DISTANCE, COST } = otherArmoveColumns(armove);
    return {
      ROOFTOP: invoice.customer?.name || '-',
      INVOICE_ID: armove.invoice_id || '-',
      MOVE_ID,
      REF_NUM,
      VIN,
      MOVE_DATE,
      STOCK,
      LANE,
      DISTANCE,
      COST,
      ...legacyBucketColumns(armove),
      CANCEL_STATUS: '-',
      MOVE_STATUS: '-',
      DISTANCE_MILES: null,
    };
  }),
];

export const generateCSV = (invoice: LegacyInvoice) => {
  if (invoice) {
    try {
      const csvRows = buildLegacyCsvRows(invoice);
      const csvOptions = {
        filename: `${invoice?.customer?.name?.replace(/ /g, '_')}_Invoice_${dayjs(invoice.start_datetime).add(1, 'day').format('MM-DD-YYYY')}_to_${dayjs(invoice.end_datetime).format('MM-DD-YYYY')}`,
        showTitle: true,
        title: `Invoice #${invoice.id}`,
        useKeysAsHeaders: true,
      };

      const csvExporter = new ExportToCsv(csvOptions);
      csvExporter.generateCsv(csvRows);
      toast.info('Generated CSV!', { autoClose: 2000, toastId: `generate-csv` });
    } catch (err) {
      toast.error('Failed to generate CSV!', { toastId: `generate-csv-error` });
      console.error('Failed to generate CSV:', err);
    }
  }
};

export const generateEnhancedCSV = (invoice: LegacyInvoice) => {
  if (invoice) {
    try {
      const csvRows = buildLegacyEnhancedCsvRows(invoice);
      const csvOptions = {
        filename: `${invoice?.customer?.name?.replace(/ /g, '_')}_Invoice_${dayjs(invoice.start_datetime).add(1, 'day').format('MM-DD-YYYY')}_to_${dayjs(invoice.end_datetime).format('MM-DD-YYYY')}`,
        showTitle: true,
        title: `Invoice #${invoice.id}`,
        useKeysAsHeaders: true,
      };

      const csvExporter = new ExportToCsv(csvOptions);
      csvExporter.generateCsv(csvRows);
      toast.info('Generated CSV!', { autoClose: 2000, toastId: `generate-csv` });
    } catch (err) {
      toast.error('Failed to generate CSV!', { toastId: `generate-csv-error` });
      console.error('Failed to generate CSV:', err);
    }
  }
};
